import React, { useEffect, useMemo, useState } from 'react';
import { Upload } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { FileUpload } from '../../../components/ui/FileUpload';
import { MAX_UPLOAD_FILE_SIZE_LABEL } from '../../../constants/fileUpload';
import customNotification from '../../../components/ui/custom-notification';
import type { MaterialData } from '../../project/types/materials';
import { DocumentTag } from '../../project/types';
import {
  getPreSignedUrlToUploadWarehouseDoc,
  updateWarehouseMaterialTraceability,
} from '../../../redux/Project/materialSaga';

type AnalysisWarehouseDocumentUploadModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onUploaded: () => void;
  material: MaterialData | null;
  documentTags: DocumentTag[];
};

export const AnalysisWarehouseDocumentUploadModal: React.FC<
  AnalysisWarehouseDocumentUploadModalProps
> = ({ isOpen, onClose, onUploaded, material, documentTags }) => {
  const [materialDocuments, setMaterialDocuments] = useState<File[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[][]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const tagOptions = useMemo(
    () =>
      documentTags
        .filter((tag) => tag.tags_type === 'MATERIAL_PIPE_TAGS')
        .map((tag) => ({
          value: tag.tags_id.toString(),
          label: tag.tags_name,
        })),
    [documentTags]
  );

  useEffect(() => {
    if (!isOpen) {
      setMaterialDocuments([]);
      setSelectedTags([]);
    }
  }, [isOpen]);

  const handleMaterialFileSelect = (files: FileList | null) => {
    if (files && files.length > 0) {
      setMaterialDocuments((prev) => [...prev, ...Array.from(files)]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setMaterialDocuments((prev) => prev.filter((_, i) => i !== index));
    setSelectedTags((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTagsChange = (fileIndex: number, tags: string[]) => {
    setSelectedTags((prev) => {
      const next = [...prev];
      next[fileIndex] = tags;
      return next;
    });
  };

  const handleUpload = async () => {
    if (!material?.materialId || materialDocuments.length === 0) return;

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setIsUploading(true);
    try {
      const presignPayload = {
        projectId: null,
        pipeId: null,
        projectTypeId: null,
        materialCategory: material.materialCategory || 'Pipe',
        materialId: material.materialId,
      };

      const s3Response = await getPreSignedUrlToUploadWarehouseDoc(
        materialDocuments,
        presignPayload,
        1
      );

      if (!s3Response?.length) {
        setIsUploading(false);
        return;
      }

      const apiPayload = {
        data: [
          {
            materialId: material.materialId,
            materialCategory: material.materialCategory || 'Pipe',
            materialStatus: material.materialStatus || 'Available',
            isWarehouseInventory: true,
            projectId: null,
            pipeId: null,
            documents: s3Response.map((file: { fileName: string; s3Key: string }, index: number) => ({
              fileName: file.fileName,
              s3Key: file.s3Key,
              version: 1,
              tags: selectedTags[index] || [],
              uploadedBy: user.email || '',
            })),
            details: material.details || {},
          },
        ],
      };

      const response = await updateWarehouseMaterialTraceability(apiPayload);
      if (response) {
        customNotification.success(
          'Documents uploaded',
          'Material documents were added to this warehouse pipe record.'
        );
        onUploaded();
        onClose();
      }
    } catch {
      customNotification.error('Upload failed', 'Could not upload documents. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[95vw] max-w-[1200px] max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden bg-white">
        <DialogHeader className="flex-shrink-0 border-b border-[#E2E5EA] px-6 py-4 pr-14">
          <DialogTitle className="flex items-center gap-2 font-heading text-[18px] font-semibold text-blueprint-blue leading-tight">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-hero-red-10">
              <Upload className="h-5 w-5 text-hero-red" />
            </span>
            Upload Pipe Material Document
          </DialogTitle>
        </DialogHeader>

        <div className="grid min-w-0 flex-1 grid-cols-4 gap-4 overflow-y-auto overflow-x-hidden p-6">
          <div className="space-y-2 col-span-4">
            <Label>Upload Documents</Label>
            <p className="text-[13px] text-text-grey">
              Upload material documents (warehouse / no project pipe setup).
              <br />
              <span className="text-[12px] text-heritage-grey">
                Allowed: PDF, Word, Excel, Text, Images — max {MAX_UPLOAD_FILE_SIZE_LABEL} per file
              </span>
            </p>

            <FileUpload
              onFileSelect={handleMaterialFileSelect}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
              multiple
              maxFiles={100}
              buttonText="Upload Documents"
              variant="outline"
              size="lg"
              onRemoveFile={handleRemoveFile}
              selectedFiles={materialDocuments}
              showFileList={materialDocuments.length > 0}
              className="w-full"
              showTags
              optionsTags={tagOptions}
              selectedTags={selectedTags}
              onTagsChange={handleTagsChange}
              maxHeight=""
            />
          </div>
        </div>

        <DialogFooter className="flex flex-shrink-0 gap-2 border-t border-[#E2E5EA] bg-page-bg px-6 py-4">
          <Button variant="outline" onClick={onClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!materialDocuments.length || isUploading}
            loading={isUploading}
            loadingText="Uploading..."
            className="bg-hero-red hover:bg-hero-red-60 text-white gap-2"
          >
            <Upload className="h-4 w-4" />
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
